<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  PContextMenu,
  PContextMenuPositionEnum,
  PFlag,
  PFlagItem,
  PFlagSize,
  PItem,
  PList,
  PSpace,
  PSpaceSize,
  usePrevioDS,
} from '@previo/design-system'
import { Locales, $t, useLocale } from '@previo/core'
import { type CountryISO3, Language } from '@previo/codebooks'
import { useRouter } from 'vue-router'
import { pmsLocaleManager } from '~/composables/translate'
import { useUserStore } from '~/store/user'
import { useUserAPI } from '~/store/user/http/UserAPI'
import { Routes } from '~/router/routes'

type Props = {
  hasIcons?: boolean
}

type Emits = {
  (e: 'navigate'): void
}

const props = withDefaults(defineProps<Props>(), {
  hasIcons: false,
})

const emit = defineEmits<Emits>()

const ds = usePrevioDS()
const router = useRouter()
const userStore = useUserStore()
const userAPI = useUserAPI()
const langMenuOpen = ref<boolean>(false)
const isChangingLanguage = ref<boolean>(false)

const changeLocale = (locale: Locales) => {
  langMenuOpen.value = false

  isChangingLanguage.value = true

  userAPI
    .changeLanguage(locale)
    .then(() => {
      pmsLocaleManager.setLocale(locale)
    })
    .catch((error) => {
      ds.toast.error(error)
    })
    .finally(() => {
      isChangingLanguage.value = false
    })
}

const currentLocaleFlag = computed<CountryISO3>(() => {
  return Language.getByLocale(useLocale().get()).countryISO3
})

const icon = (name: string): string | undefined => {
  return props.hasIcons ? name : undefined
}

const navigateToUserDetail = () => {
  router.push({ name: Routes.USERS_DETAIL, params: { userId: userStore.user.id } })
  emit('navigate')
}

const navigateToNotificationsSettings = (): void => {
  router.push({ name: Routes.SETTINGS_NOTIFICATIONS_RESERVATION })
  emit('navigate')
}
</script>

<template>
  <p-item
    :text="$t('Edit user')"
    :icon-start="icon('pen')"
    cy="edit-user"
    @click="navigateToUserDetail"
  />
  <p-item
    :text="$t('Notifications')"
    :icon-start="icon('bell')"
    cy="notifications-settings"
    @click="navigateToNotificationsSettings"
  />
  <p-item
    :text="$t('Change password')"
    :icon-start="icon('lock')"
    cy="change-password"
    @click="navigateToUserDetail"
  />
  <p-item
    :text="$t('Change language')"
    :icon-start="icon('language')"
    :loading="isChangingLanguage"
    cy="change-language"
    @click="langMenuOpen = !langMenuOpen"
  >
    <template v-if="hasIcons">
      <p-space :mobile="PSpaceSize.SM" />
      <p-flag :country-iso3="currentLocaleFlag" :size="PFlagSize.SMALL" />
    </template>

    <p-context-menu
      v-model="langMenuOpen"
      :title="$t('Change language')"
      :anchor="PContextMenuPositionEnum.BOTTOM_START"
      :self="PContextMenuPositionEnum.TOP_START"
      cy="select-language"
    >
      <p-list>
        <p-flag-item
          text="Čeština"
          :language-shortcut="Locales.CS"
          cy="czech"
          clickable
          @click="changeLocale(Locales.CS)"
        />
        <p-flag-item
          text="English"
          :language-shortcut="Locales.EN"
          cy="english"
          clickable
          @click="changeLocale(Locales.EN)"
        />
        <p-flag-item
          text="Hrvatski"
          :language-shortcut="Locales.HR"
          cy="croatian"
          clickable
          @click="changeLocale(Locales.HR)"
        />
        <p-flag-item
          text="Magyar"
          :language-shortcut="Locales.HU"
          cy="hungary"
          clickable
          @click="changeLocale(Locales.HU)"
        />
        <p-flag-item
          text="Polski"
          :language-shortcut="Locales.PL"
          cy="polish"
          clickable
          @click="changeLocale(Locales.PL)"
        />
        <p-flag-item
          text="Deutsch"
          :language-shortcut="Locales.DE"
          cy="german"
          clickable
          @click="changeLocale(Locales.DE)"
        />
        <p-flag-item
          text="Românesc"
          :language-shortcut="Locales.RO"
          cy="romanian"
          clickable
          @click="changeLocale(Locales.RO)"
        />
        <p-flag-item
          text="Slovenčina"
          :language-shortcut="Locales.SK"
          cy="slovak"
          clickable
          @click="changeLocale(Locales.SK)"
        />
        <p-flag-item
          text="Español"
          :language-shortcut="Locales.ES"
          cy="spanish"
          clickable
          @click="changeLocale(Locales.ES)"
        />
        <p-flag-item
          text="български"
          :language-shortcut="Locales.BG"
          cy="bulgarian"
          clickable
          @click="changeLocale(Locales.BG)"
        />
        <p-flag-item
          text="Italiano"
          :language-shortcut="Locales.IT"
          cy="italian"
          clickable
          @click="changeLocale(Locales.IT)"
        />
        <p-flag-item
          text="Українська"
          :language-shortcut="Locales.UK"
          cy="ukrainian"
          clickable
          @click="changeLocale(Locales.UK)"
        />
        <p-flag-item
          text="Português"
          :language-shortcut="Locales.PT"
          cy="portuguese"
          clickable
          @click="changeLocale(Locales.PT)"
        />
      </p-list>
    </p-context-menu>
  </p-item>
</template>
